<script setup lang="ts">
import EstateListingHeaderMostSearched from './EstateListingHeaderMostSearched.vue'
import EstateListingHeaderDescription from './EstateListingHeaderDescription.vue'
import type { useFilterState } from '~/composables/filter/useFilterState'
import type {
  EstateListingResponse,
  EstateType,
} from '~/types/api/EstateListing'

const props = defineProps<{
  officesResponse: EstateListingResponse | null
  officesPending: boolean
  estateType: EstateType
  filterState: ReturnType<typeof useFilterState>
}>()

const { locale } = useI18n()

const route = useRoute()

const subtitleKeyPath = computed(() => {
  if (Object.keys(props.filterState.filterValues.value).length > 0) {
    return 'pages.estateListing.header.subtitle.baseFiltered'
  }

  return 'pages.estateListing.header.subtitle.base'
})

const currentCity = computed(() => {
  return getCityName(props.filterState.filterValues.value.city)
})

const warehouseContent = computed(() => {
  if (props.estateType !== 'warehouse') return

  return getWarehouseListingContent(
    locale.value,
    route.params?.city as string | undefined
  )
})
</script>

<template>
  <div class="c-header-main">
    <h1 v-if="warehouseContent" class="c-header-main__title h2">
      {{ warehouseContent.h1 }}
    </h1>

    <h1 v-else class="c-header-main__title h2">
      {{ $t(`pages.estateListing.header.titles.${estateType}`) }}
      {{ $t(`pages.estateListing.header.titles.forRent`) }}
      {{
        Object.keys(props.filterState.filterValues.value).length !== 0 &&
        currentCity
          ? currentCity[locale.toLowerCase() as 'en' | 'sk']
          : ''
      }}
    </h1>

    <i18n-t
      :keypath="subtitleKeyPath"
      tag="div"
      scope="global"
      class="c-header-main__subtitle"
    >
      <template #_0>
        <span v-if="!officesResponse && officesPending" class="sc-skeleton" />

        <strong v-else-if="officesResponse">
          {{
            $t(
              'pages.estateListing.header.subtitle.0',
              { _0: officesResponse.count },
              officesResponse.count
            )
          }}
        </strong>
      </template>
    </i18n-t>

    <p
      v-if="
        props.estateType === 'office' && !route.params?.city && locale === 'sk'
      "
      class="c-header-main__description"
    >
      Ak pre seba, svoj tím či firmu hľadáte kancelárske priestory ste na
      správnom mieste. Proces výberu vhodného priestoru je často veľmi náročný,
      no okrem toho je aj veľmi rozhodujúcim krokom pre úspešné podnikanie.
      Možno ste rastúci startup, etablovaná firma alebo hľadáte nový kancelársky
      priestor pre vašu organizáciu –
      <strong>
        to, na akom mieste si prenajmete kanceláriu a aká bude jej flexibilita,
        je kľúčové.
      </strong>
      Práve preto vám my z
      <NuxtLink class="c-link" to="/"> CBRE Firemné reality </NuxtLink>
      <strong>
        ponúkame širokú škálu možností pre prenájom kancelárií, ktoré reflektujú
        potreby
      </strong>
      moderného biznisu, meniacich sa pracovných štýlov, no hlavne, vás. Okrem
      prenájmu kancelárskych priestorov u nás nájdete aj
      <NuxtLink class="c-link" to="/obchodne-priestory">
        obchodné priestory</NuxtLink
      >
      či
      <NuxtLink class="c-link" to="/sklady-haly-a-pozemky">
        sklady, haly a pozemky
      </NuxtLink>
    </p>

    <p
      v-else-if="
        props.estateType === 'office' && route.params?.city && locale === 'sk'
      "
      class="c-header-main__description"
    >
      Ak pre seba, svoj tím či firmu hľadáte kancelárske priestory v meste
      {{ route.params.city }}, ste na správnom mieste. Proces výberu vhodného
      priestoru je často veľmi náročný, no okrem toho je aj veľmi rozhodujúcim
      krokom pre úspešné podnikanie. Možno ste rastúci startup, etablovaná firma
      alebo hľadáte nový kancelársky priestor pre vašu organizáciu –
      <strong>
        to, na akom mieste si prenajmete kanceláriu a aká bude jej flexibilita,
        je kľúčové.
      </strong>
      Práve preto vám my z
      <NuxtLink class="c-link" to="/"> CBRE Firemné reality </NuxtLink>
      ponúkame širokú škálu možností pre prenájom kancelárií v meste
      {{ route.params.city }}, ktoré reflektujú potreby moderného biznisu,
      meniacich sa pracovných štýlov, no hlavne, vás. Okrem prenájmu
      kancelárskych priestorov u nás nájdete aj
      <NuxtLink class="c-link" to="/obchodne-priestory">
        obchodné priestory
      </NuxtLink>
      ,
      <NuxtLink class="c-link" to="/sklady-haly-a-pozemky">
        sklady, haly a pozemky
      </NuxtLink>
    </p>
    <EstateListingHeaderDescription
      v-else-if="warehouseContent && !route.params?.city"
      class="c-header-main__description"
    >
      <p>
        V
        <NuxtLink class="c-link" to="/">CBRE Firemné reality</NuxtLink>
        <strong>
          sprostredkúvame prenájom skladov, hál a výrobných priestorov po celom
          Slovensku</strong
        >. Sme experti v oblasti komerčných nehnuteľností a
        <strong>spájame dopyt s kvalitnou ponukou už viac ako 23 rokov</strong>.
      </p>
      <p>
        V našej ponuke skladov a hál
        <strong>
          nájdete menšie priestory aj rozsiahle logistické a výrobné
          priestory</strong
        >, ktorých technické parametre poznáme z vlastnej obhliadky, nielen z
        inzerátu.
      </p>
      <p>
        Pri každom priestore nájdete uvedené
        <strong>technické parametre</strong>, ako sú celková aj minimálna
        prenajímateľná výmera, nosnosť podlahy, rozpon stĺpov, vzdialenosť k
        najbližšiemu výjazdu na diaľnicu
        <strong>
          a ďalšie dôležité informácie, ktoré sú nevyhnutné pri prenájme
        </strong>
        ideálneho skladového priestoru či haly.
      </p>
      <p>
        Filter nad ponukou umožňuje vybrať lokalitu, veľkosť aj typ priestoru a
        systém vám zobrazí len tie ponuky, ktoré reálne zodpovedajú vašim
        požiadavkám.
      </p>
      <p>
        <strong>
          Vyberte si z aktuálnej ponuky nižšie alebo nám rovno napíšte svoje
          požiadavky
        </strong>
        na sklad či halu.
        <strong>Ponuku na mieru vám pripravíme zdarma</strong>.
      </p>
    </EstateListingHeaderDescription>

    <EstateListingHeaderDescription
      v-else-if="warehouseContent?.paragraphs"
      class="c-header-main__description"
    >
      <p v-for="(paragraph, index) in warehouseContent.paragraphs" :key="index">
        {{ paragraph }}
      </p>
    </EstateListingHeaderDescription>

    <p
      v-else-if="props.estateType === 'warehouse' && locale === 'sk'"
      class="c-header-main__description"
    >
      Podnikateľské aktivity si vyžadujú aj ideálny priestor. Ak aj vy hľadáte
      sklady, haly alebo pozemky na prenájom, u nás v
      <NuxtLink class="c-link" to="/"> CBRE Firemné reality </NuxtLink>
      ste na správnom mieste. Sme experti v oblasti komerčných nehnuteľností a
      spájame dopyt s kvalitnou ponukou po celom Slovensku – od Bratislavy, cez
      Nitru, Žilinu až po Košice.<br /><br />
      Naša ponuka je pestrá, okrem skladov, hál a pozemkov na prenájom či predaj
      nájdete tiež
      <NuxtLink class="c-link" to="/kancelarie">
        kancelárske priestory
      </NuxtLink>
      a
      <NuxtLink class="c-link" to="/obchodne-priestory">
        obchodné priestory </NuxtLink
      >.<br /><br />
      Vďaka prehľadnému vyhľadávaniu si môžete jednoducho vyfiltrovať, čo presne
      potrebujete – lokalitu, typ ponuky, veľkosť aj druh ponuky. Následne si
      stačí len vybrať, čo najlepšie spĺňa vaše požiadavky – či to bude prenájom
      malého skladu v Bratislave alebo prenájom poľnohospodárskej pôdy.
    </p>

    <div class="c-header-main__divider" />

    <div class="c-header-main__most-searched">
      <EstateListingHeaderMostSearched :estate-type="estateType" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;
@use '@/assets/styles/mixins/skeleton' as skeleton;

.c-header-main {
  color: var(--color-light);

  &__title {
    margin-bottom: 6px;

    @include breakpoint-down('xs') {
      font-size: 36px;
    }
  }

  &__subtitle {
    line-height: 1.2;

    .sc-skeleton {
      @include skeleton.skeleton;

      --skeleton-background-color: hsla(
        var(--color-light-h),
        var(--color-light-s),
        var(--color-light-l),
        0.1
      );
      --skeleton-loader-opacity: 0.2;
      --skeleton-loader-color: var(--color-light);

      display: inline-block;
      width: 77px;
      height: 1.2em;
      vertical-align: bottom;
      border-radius: 2px;

      @include breakpoint-down('xs') {
        display: block;
      }
    }

    strong {
      display: inline-block;

      @include breakpoint-down('xs') {
        display: block;
      }
    }
  }

  &__description {
    & {
      padding-top: 1.4rem;
      margin: 0;
    }

    .c-link {
      & {
        color: var(--color-secondary);
        font-weight: 600;
      }

      &:hover {
        & {
          text-decoration: underline;
        }
      }
    }
  }

  &__divider {
    margin: 40px 0 24px;
    height: 2px;

    @include breakpoint-down('lg') {
      display: none;
    }

    &::before {
      content: '';
      display: block;
      height: 100%;
      margin-left: calc(-50vw + 650px);
      background-color: var(--color-secondary);

      @include breakpoint-down('xl') {
        margin-left: calc(-50vw + 544px);
      }
    }
  }

  &__most-searched {
    @include breakpoint-down('xl') {
      margin-top: 20px;
    }
  }
}
</style>
